import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import { toTitleCase } from '../../../utils/stringUtils';
import tune from '../../../assets/tune.svg';
import ErrorState from '../../../components/ui/ErrorState';
import SearchFilterBar from '../../../components/ui/SearchFilterBar';
import FiltersModal from '../../../components/ui/FiltersModal';
import Pagination from '../../../components/ui/Pagination';
import { appraisalAdminService, authService } from '../../../services';
import { apiService } from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import showToast from '../../../utils/toast';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';
import { useRBAC } from '../../../hooks/useRBAC';
import { CycleType } from '../../../types';

interface EmployeeRow {
	id: string;
	employeeId: string;
	name: string;
	email: string;
	status: 'Completed' | 'Pending' | 'In Progress' | 'Not Started' | 'Approved' | 'Rejected';
	okrStatus: 'Completed' | 'Pending' | 'In Progress' | 'Not Started' | 'Approved' | 'Rejected';
	department: string;
	designation: string;
}


const PageSizeOptions = [10, 25, 50];

/**
 * Maps raw backend OKR status to a display label based on the current cycle type.
 */
const getOkrDisplayStatus = (rawStatus: string, cycleType?: number): string => {
	const status = rawStatus?.toLowerCase();
	switch (cycleType) {
		case CycleType.START: // Cycle 0 - Target Setting
			if (status === 'draft') return 'Pending OKR submission';
			if (status === 'pending') return 'Pending OKR approval';
			if (status === 'approved') return 'OKR approved';
			if (status === 'rejected') return 'OKR rejected';
			if (status === 'n/a') return 'N/A';
			break;
		case CycleType.MID: // Cycle 1 - Mid-Year
			if (status === 'draft') return 'Pending mid-year OKR submission';
			if (status === 'pending') return 'Pending mid-year OKR approval';
			if (status === 'approved') return 'Mid-year OKR approved';
			if (status === 'rejected') return 'Mid-year OKR rejected';
			if (status === 'n/a') return 'N/A';
			break;
		case CycleType.END: // Cycle 2 - End-Year
			if (status === 'draft') return 'Pending end-year OKR submission';
			if (status === 'pending') return 'Pending end-year OKR approval';
			if (status === 'rejected') return 'End-year OKR rejected';
			if (status === 'approved') return 'End-year OKR approved';
			if (status === 'completed') return 'Completed';
			if (status === 'n/a') return 'N/A';
			break;
	}
	return rawStatus; // Fallback to raw status if no mapping found
};

const AppraisalAdminPage: React.FC = () => {
	const navigate = useNavigate();
	const hasActiveCycle = useActiveCycle();
	const { appraisalCycle } = useRBAC();
	const cycleType = appraisalCycle?.cycle_type;
	const [rows, setRows] = useState<EmployeeRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [errorType, setErrorType] = useState<'server-error' | 'bad-gateway' | 'timeout' | null>(null);
	const fileInputRef = React.useRef<HTMLInputElement | null>(null);
	const [query, setQuery] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState<number>(10);
	
	// Track if data has been fetched to prevent duplicate API calls
	const hasFetched = React.useRef(false);

	// Filters modal + state
	const [showFilters, setShowFilters] = useState(false);
	const [tempFilters, setTempFilters] = useState({ department: '', designation: '', nominationCapacity: '', okrStatus: '' });
	const [appliedFilters, setAppliedFilters] = useState({ department: '', designation: '', nominationCapacity: '', okrStatus: '' });

	// Listen for global clearFilters event from SearchFilterBar
	useEffect(() => {
		const handler = () => {
			setTempFilters({ department: '', designation: '', nominationCapacity: '', okrStatus: '' });
			setAppliedFilters({ department: '', designation: '', nominationCapacity: '', okrStatus: '' });
			setQuery('');
			setPage(1);
		};

		window.addEventListener('clearFilters', handler as EventListener);
		return () => window.removeEventListener('clearFilters', handler as EventListener);
	}, []);

	const uniqueDepartments = useMemo(() => Array.from(new Set(rows.map(r => r.department).filter(Boolean))), [rows]);
	const uniqueDesignations = useMemo(() => Array.from(new Set(rows.map(r => r.designation).filter(Boolean))), [rows]);
	
	// Status options dynamically from API data
	const uniqueNominationCapacities = useMemo(() => Array.from(new Set(rows.map(r => r.status).filter(Boolean))).sort(), [rows]);
	const uniqueOkrStatuses = useMemo(() => Array.from(new Set(rows.map(r => getOkrDisplayStatus(r.okrStatus, cycleType)).filter(Boolean))).sort(), [rows, cycleType]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return rows.filter(r => {
			// search term match
			if (q) {
				const matchesSearch = (
					r.employeeId.toLowerCase().includes(q) ||
					r.name.toLowerCase().includes(q) ||
					r.department.toLowerCase().includes(q) ||
					r.designation.toLowerCase().includes(q)
				);
				if (!matchesSearch) return false;
			}

			// applied filters
			if (appliedFilters.department && r.department !== appliedFilters.department) return false;
			if (appliedFilters.designation && r.designation !== appliedFilters.designation) return false;
			if (appliedFilters.nominationCapacity && r.status?.toLowerCase() !== appliedFilters.nominationCapacity.toLowerCase()) return false;
			if (appliedFilters.okrStatus && getOkrDisplayStatus(r.okrStatus, cycleType).toLowerCase() !== appliedFilters.okrStatus.toLowerCase()) return false;

			return true;
		});
	}, [rows, query, appliedFilters, cycleType]);

	const openEmployeeDetails = (row: EmployeeRow) => {
		navigate(
			{
				pathname: `/appraisal-admin/${row.employeeId}`,
			},
			{ state: { employee: row, employeeEmail: row.email } },
		);
	};

	useEffect(() => {
		// Prevent duplicate API calls
		if (hasFetched.current) {
			return;
		}
		hasFetched.current = true;
		
		const load = async () => {
			const userEmail = authService.getStoredUser()?.email;
			if (!userEmail) {
				setError('User email not available');
				return;
			}
			
			setLoading(true);
			setError(null);
	    try {
				const resp = await appraisalAdminService.getEmployeeList(userEmail);
				const data = resp.data || [];
				const mapped: EmployeeRow[] = data.map((d, idx) => ({
					id: String(d.sr_no ?? idx + 1),
					employeeId: d.emp_id ?? '',
					name: d.emp_name ?? '',
					email: d.emp_email ?? '',
		    status: (d.emp_appraisal_status as string) as EmployeeRow['status'] ?? 'Not Started',
					okrStatus: ((d as any).emp_okr_status as string) as EmployeeRow['okrStatus'] ?? 'Not Started',
					department: d.emp_department ?? '',
					designation: d.emp_designation ?? '',
				}));
				setRows(mapped);
			} catch (err: unknown) {
				console.error('Failed to load admin employees', err);
				let message = 'Failed to fetch employees';
				let type: 'server-error' | 'bad-gateway' | 'timeout' = 'server-error';
				
				// Check for specific error codes
				if (err && typeof err === 'object' && 'status_code' in err) {
					const statusCode = (err as { status_code: number }).status_code;
					if (statusCode === 401) {
						// Session expired - redirect to login is already handled by api interceptor
						return;
					}
					if (statusCode === 502) {
						type = 'bad-gateway';
						message = 'Server temporarily unavailable. Please try again.';
					} else if (statusCode === 504) {
						type = 'timeout';
						message = 'Request timed out. Please try again.';
					}
				}
				
				if (err instanceof Error) {
					message = err.message || message;
				} else if (err && typeof err === 'object' && 'message' in err) {
					const maybe = err as unknown as { message?: unknown };
					if (typeof maybe.message === 'string') message = maybe.message;
					else message = String(err);
				} else {
					message = String(err);
				}
				
				setError(message || 'Failed to fetch employees');
				setErrorType(type);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, []);

	// File input handler for CSV and Excel import
	const handleImportFile = async (file?: File) => {
		if (!file) return;
		
		// Validate file type
		const validExtensions = ['.csv', '.xlsx', '.xls'];
		const fileName = file.name.toLowerCase();
		const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));
		
		if (!isValidFile) {
			showToast('Please upload a CSV or Excel file (.csv, .xlsx, .xls)', 'error');
			// Reset file input so the same file can be selected again
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
			return;
		}
		
		const userEmail = authService.getStoredUser()?.email;
		if (!userEmail) {
			alert('User email not available');
			// Reset file input
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
			return;
		}
		try {
			setIsImporting(true);
			const resp = await appraisalAdminService.uploadEmployeeFile(userEmail, file, () => {});
			showToast(resp.message || 'Upload successful', 'success');
			// refresh list
			const refreshed = await appraisalAdminService.getEmployeeList(userEmail);
			const data = refreshed.data || [];
			const mapped: EmployeeRow[] = data.map((d, idx) => ({
				id: String(d.sr_no ?? idx + 1),
				employeeId: d.emp_id ?? '',
				name: d.emp_name ?? '',
				email: d.emp_email ?? '',
				status: (d.emp_appraisal_status as string) as EmployeeRow['status'] ?? 'Not Started',
				okrStatus: ((d as any).emp_okr_status as string) as EmployeeRow['okrStatus'] ?? 'Not Started',
				department: d.emp_department ?? '',
				designation: d.emp_designation ?? '',
			}));
			setRows(mapped);
		} catch (err: unknown) {
			console.error('Upload failed', err);
			if (err && typeof err === 'object' && 'status_code' in err) {
				if ((err as { status_code: number }).status_code === 401) return;
			}
			let message = 'Upload failed';
			if (err instanceof Error) message = err.message;
			else if (err && typeof err === 'object' && 'message' in err) {
				const maybe = err as unknown as { message?: unknown };
				if (typeof maybe.message === 'string') message = maybe.message;
				else message = String(err);
			} else {
				message = String(err);
			}
			showToast(message || 'Upload failed', 'error');
		} finally {
			setIsImporting(false);
			// Reset file input so the same file (or any file) can be selected again
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	};

	// Export handler - preserves original file type from backend
	const handleExportData = async () => {
		const userEmail = authService.getStoredUser()?.email;
		if (!userEmail) {
			showToast('User email not available', 'error');
			return;
		}
		
		try {
			setIsExporting(true);
			
			// Call the export API directly using the raw axios instance to handle blob properly
			const encodedEmail = encodeURIComponent(userEmail);
			const axiosInstance = apiService.getAxiosInstance();
			
			const response = await axiosInstance.get(`/api/v1/feedback/admin/export/?email=${encodedEmail}`, {
				responseType: 'blob',
			});
			
			// Ensure we have blob data
			if (!response.data) {
				throw new Error('No data received from export API');
			}
			
			// Get content type and filename from response headers
			const contentType = response.headers['content-type'] || 'application/octet-stream';
			const contentDisposition = response.headers['content-disposition'];
			
			// Create blob with original content type from backend
			const blob = new Blob([response.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			
			// Create temporary download link
			const link = document.createElement('a');
			link.href = url;
			
			// Try to extract filename from Content-Disposition header
			let filename = '';
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}
			
			// If no filename from header, generate one with appropriate extension based on content type
			if (!filename) {
				const currentDate = new Date().toISOString().split('T')[0];
				let extension = 'xlsx'; // Default to xlsx
				
				if (contentType.includes('csv')) {
					extension = 'csv';
				} else if (contentType.includes('spreadsheetml') || contentType.includes('excel') || contentType.includes('xlsx')) {
					extension = 'xlsx';
				} else if (contentType.includes('ms-excel') || contentType.includes('xls')) {
					extension = 'xls';
				}
				
				filename = `appraisal_export_${currentDate}.${extension}`;
			}
			
			link.download = filename;
			
			// Append to body, click, and cleanup
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			
			// Clean up the blob URL
			window.URL.revokeObjectURL(url);
			
			showToast('Export successful! File has been downloaded.', 'success');
			
		} catch (err: unknown) {
			console.error('Export failed', err);
			if (err && typeof err === 'object' && 'status_code' in err) {
				if ((err as { status_code: number }).status_code === 401) return;
			}
			let message = 'Export failed';
			
			if (err instanceof Error) {
				message = err.message;
			} else if (err && typeof err === 'object' && 'message' in err) {
				const maybe = err as unknown as { message?: unknown };
				if (typeof maybe.message === 'string') {
					message = maybe.message;
				} else {
					message = String(err);
				}
			} else {
				message = String(err);
			}
			
			showToast(message || 'Export failed', 'error');
		} finally {
			setIsExporting(false);
		}
	};
// Export handler - preserves original file type from backend
	const handleDownloadData = async () => {
		const userEmail = authService.getStoredUser()?.email;
		if (!userEmail) {
			showToast('User email not available', 'error');
			return;
		}
		
		try {
			setIsDownloading(true);
			
			// Call the download API directly using the raw axios instance to handle blob properly
			const encodedEmail = encodeURIComponent(userEmail);
			const axiosInstance = apiService.getAxiosInstance();
			
			const response = await axiosInstance.get(`/api/v1/feedback/admin/download/?email=${encodedEmail}`, {
				responseType: 'blob',
			});
			
			// Ensure we have blob data
			if (!response.data) {
				throw new Error('No data received from export API');
			}
			
			// Get content type and filename from response headers
			const contentType = response.headers['content-type'] || 'application/octet-stream';
			const contentDisposition = response.headers['content-disposition'];
			
			// Create blob with original content type from backend
			const blob = new Blob([response.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			
			// Create temporary download link
			const link = document.createElement('a');
			link.href = url;
			
			// Try to extract filename from Content-Disposition header
			let filename = '';
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}
			
			// If no filename from header, generate one with appropriate extension based on content type
			if (!filename) {
				const currentDate = new Date().toISOString().split('T')[0];
				let extension = 'xlsx'; // Default to xlsx
				
				if (contentType.includes('csv')) {
					extension = 'csv';
				} else if (contentType.includes('spreadsheetml') || contentType.includes('excel') || contentType.includes('xlsx')) {
					extension = 'xlsx';
				} else if (contentType.includes('ms-excel') || contentType.includes('xls')) {
					extension = 'xls';
				}
				
				filename = `appraisal_export_${currentDate}.${extension}`;
			}
			
			link.download = filename;
			
			// Append to body, click, and cleanup
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			
			// Clean up the blob URL
			window.URL.revokeObjectURL(url);
			
			showToast('Download successful! File has been downloaded.', 'success');
			
		} catch (err: unknown) {
			console.error('Download failed', err);
			if (err && typeof err === 'object' && 'status_code' in err) {
				if ((err as { status_code: number }).status_code === 401) return;
			}
			let message = 'Download failed';
			
			if (err instanceof Error) {
				message = err.message;
			} else if (err && typeof err === 'object' && 'message' in err) {
				const maybe = err as unknown as { message?: unknown };
				if (typeof maybe.message === 'string') {
					message = maybe.message;
				} else {
					message = String(err);
				}
			} else {
				message = String(err);
			}
			
			showToast(message || 'Download failed', 'error');
		} finally {
			setIsDownloading(false);
		}
	};

	const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
	const pageStart = (page - 1) * pageSize;
	const pageRows = filtered.slice(pageStart, pageStart + pageSize);

	const handleRetry = async () => {
		const userEmail = authService.getStoredUser()?.email;
		if (!userEmail) {
			setError('User email not available');
			return;
		}
		setLoading(true);
		setError(null);
		setErrorType(null);
		try {
			const resp = await appraisalAdminService.getEmployeeList(userEmail);
			const data = resp.data || [];
			const mapped: EmployeeRow[] = data.map((d, idx) => ({
				id: String(d.sr_no ?? idx + 1),
				employeeId: d.emp_id ?? '',
				name: d.emp_name ?? '',
				email: d.emp_email ?? '',
				status: (d.emp_appraisal_status as string) as EmployeeRow['status'] ?? 'Not Started',
				okrStatus: ((d as any).emp_okr_status as string) as EmployeeRow['okrStatus'] ?? 'Not Started',
				department: d.emp_department ?? '',
				designation: d.emp_designation ?? '',
			}));
			setRows(mapped);
		} catch (err: unknown) {
			console.error('Failed to load admin employees', err);
			
			// Check for specific error codes
			if (err && typeof err === 'object' && 'status_code' in err) {
				const statusCode = (err as { status_code: number }).status_code;
				if (statusCode === 401) {
					// Session expired - redirect to login is already handled by api interceptor
					return;
				}
				if (statusCode === 502) {
					navigate('/error', {
						state: {
							errorType: 'bad-gateway',
							previousUrl: window.location.pathname,
							retryUrl: window.location.pathname,
							retryAction: async () => {
								const userEmail = authService.getStoredUser()?.email;
								if (userEmail) {
									const resp = await appraisalAdminService.getEmployeeList(userEmail);
									const data = resp.data || [];
									const mapped: EmployeeRow[] = data.map((d, idx) => ({
										id: String(d.sr_no ?? idx + 1),
										employeeId: d.emp_id ?? '',
										name: d.emp_name ?? '',
										email: d.emp_email ?? '',
										status: (d.emp_appraisal_status as string) as EmployeeRow['status'] ?? 'Not Started',
										okrStatus: ((d as any).emp_okr_status as string) as EmployeeRow['okrStatus'] ?? 'Not Started',
										department: d.emp_department ?? '',
										designation: d.emp_designation ?? '',
									}));
									setRows(mapped);
								}
							}
						}
					});
					return;
				}
			}
			
			let message = 'Failed to fetch employees';
			let type: 'server-error' | 'bad-gateway' | 'timeout' = 'server-error';
			
			// Check for other specific error codes
			if (err && typeof err === 'object' && 'status_code' in err) {
				const statusCode = (err as { status_code: number }).status_code;
				if (statusCode === 504) {
					type = 'timeout';
					message = 'Request timed out. Please try again.';
				}
			}
			
			if (err instanceof Error) {
				message = err.message || message;
			} else if (err && typeof err === 'object' && 'message' in err) {
				const maybe = err as unknown as { message?: unknown };
				if (typeof maybe.message === 'string') message = maybe.message;
				else message = String(err);
			} else {
				message = String(err);
			}
			
			setError(message || 'Failed to fetch employees');
			setErrorType(type);
		} finally {
			setLoading(false);
		}
	};

	if (!hasActiveCycle) {
		return <NoActiveCycle />;
	}

	// Show error page if there's an error and no data
	if (error && rows.length === 0 && !loading) {
		return (
			<ErrorState
				type={errorType || 'server-error'}
				title={errorType === 'bad-gateway' ? 'Bad Gateway' : errorType === 'timeout' ? 'Request Timeout' : 'Oops! Something went wrong'}
				message={error}
				onRetry={handleRetry}
				showRetryButton={true}
			/>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
				<SearchFilterBar
					searchTerm={query}
					onSearchChange={(value) => { setQuery(value); setPage(1); }}
					onClearSearch={() => { setQuery(''); setPage(1); }}
					onOpenFilters={() => { setTempFilters(appliedFilters); setShowFilters(true); }}
					hasActiveFilters={!!(appliedFilters.department || appliedFilters.designation || appliedFilters.nominationCapacity || appliedFilters.okrStatus)}
					activeFiltersCount={[appliedFilters.department, appliedFilters.designation, appliedFilters.nominationCapacity, appliedFilters.okrStatus].filter(Boolean).length}
					tuneIcon={tune}
				/>
				{/* Filters modal for admin */}
				<FiltersModal
					show={showFilters}
					tempFilters={tempFilters}
					uniqueDepartments={uniqueDepartments}
					uniqueDesignations={uniqueDesignations}
					uniqueNominationCapacities={uniqueNominationCapacities}
					uniqueOkrStatuses={uniqueOkrStatuses}
					onChange={(type, value) => setTempFilters(prev => ({ ...prev, [type]: value }))}
					onApply={() => { setAppliedFilters(tempFilters); setShowFilters(false); }}
					onRemoveAll={() => { setTempFilters({ department: '', designation: '', nominationCapacity: '', okrStatus: '' }); setAppliedFilters({ department: '', designation: '', nominationCapacity: '', okrStatus: '' }); }}
					onClose={() => { setShowFilters(false); setTempFilters(appliedFilters); }}
				/>
				<div className="flex w-full flex-nowrap items-center justify-end gap-2 overflow-x-auto pb-1 sm:gap-3 xl:w-max xl:flex-none xl:overflow-visible">
					<div className="shrink-0">
						<>
							
							<Button
								onClick={handleDownloadData}
								title="Download Employee Data (CSV or Excel)"
								aria-label="Download Employee Data"
								variant="outline"
								className="text-purple-600 border-purple-600 hover:bg-purple-50 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={isDownloading}
							>
								{isDownloading ? 'Downloading...' : 'Download'}
							</Button>
						</>
					</div>
					<div className="shrink-0">
						<>
							<input
								type="file"
								accept=".csv,.xlsx,.xls"
								ref={fileInputRef}
								className="hidden"
								onChange={(e) => handleImportFile(e.target.files ? e.target.files[0] : undefined)}
							/>
							<Button
								onClick={() => fileInputRef.current?.click()}
								title="Import employee data (CSV or Excel)"
								aria-label="Import employee data"
								className="bg-purple-800 text-white px-4 py-2"
								disabled={isImporting}
							>
								{isImporting ? 'Importing...' : 'Import'}
							</Button>
						</>
					</div>
					<div className="shrink-0">
						<Button
							onClick={handleExportData}
							title="Export employee data (CSV)"
							aria-label="Export employee data"
							className="bg-purple-800 text-white px-4 py-2"
							disabled={loading || isExporting}
						>
							{isExporting ? 'Exporting...' : 'Export'}
						</Button>
					</div>
				</div>
			</div>

			{/* Table Card */}
			<Card>
				<div className="table-container">
					{/* Table Header */}
					<div className="bg-primary-800 rounded-lg px-6 py-3 border-b border-gray-200 mb-2">
						<div className="grid grid-cols-13 gap-4 text-left text-base font-medium text-white tracking-wider">
							<div className="text-base col-span-1">SNO.</div>
							<div className="text-base col-span-2">Employee ID</div>
							<div className="text-base col-span-2">Employee Name</div>
							<div className="text-base col-span-2">GW 360 Feedback Statuses</div>
							<div className="text-base col-span-2">OKR Status</div>
							<div className="text-base col-span-2">Department</div>
							<div className="text-base col-span-2">Designation</div>
						</div>
					</div>

					{/* Table Body */}
					<div className="space-y-3 relative">
						{loading ? (
							<div className="text-center py-12">
								<div className="flex items-center justify-center">
									<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mr-3"></div>
									<span className="text-gray-600">Loading employees...</span>
								</div>
							</div>
						) : pageRows.length === 0 ? (
							<div className="text-center py-12">
								<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
									<svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
									</svg>
								</div>
								<h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
								<p className="text-gray-500 mb-4">
									{filtered.length === 0 && rows.length > 0 
										? "No employees match your search criteria. Try adjusting your filters or search term."
										: "No employees have been added yet. Import employee data to get started."
									}
								</p>
							</div>
						) : (
							pageRows.map((r, idx) => (
								<div 
									key={r.id} 
									className="hover:bg-gray-50 border border-gray-200 rounded-lg px-6 py-1 transition-colors relative cursor-pointer"
									onClick={() => openEmployeeDetails(r)}
									tabIndex={0}
									onKeyDown={(e) => { if (e.key === 'Enter') { openEmployeeDetails(r); } }}
								>
									<div className="grid grid-cols-13 gap-4 items-center">
										<div className="col-span-1">
											<div className="text-base text-gray-600">{pageStart + idx + 1}.</div>
										</div>
										<div className="col-span-2">
											<div className="text-base text-gray-600">{r.employeeId}</div>
										</div>
										<div className="col-span-2">
											<NameWithEmailTooltip
												as="div"
												className="text-base text-gray-600"
												name={toTitleCase(r.name)}
												email={r.email}
											/>
										</div>
										<div className="col-span-2">
										{(() => {
											const raw = r.status?.trim().toLowerCase();
											const display = r.status?.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
											console.log('Rendering status for', r.name, 'raw:', raw, 'display:', display);
										
											if (raw === 'approved' || raw === 'nomination approved' || raw === 'completed') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{display}</span>;
											}
											if (raw === 'rejected') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{display}</span>;
											}
											if (raw === 'pending') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{display}</span>;
											}
											if (raw === 'draft') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{display}</span>;
											}
											if (raw === 'n/a') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">N/A</span>;
											}
											return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{display}</span>;
										})()}
										</div>
										<div className="col-span-2">
										{(() => {
											const displayStatus = getOkrDisplayStatus(r.okrStatus, cycleType);
											const raw = r.okrStatus?.toLowerCase();
											if (raw === 'approved' || raw === 'completed') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{displayStatus}</span>;
											}
											if (raw === 'rejected') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{displayStatus}</span>;
											}
											if (raw === 'pending') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{displayStatus}</span>;
											}
											if (raw === 'n/a') {
												return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">N/A</span>;
											}
											return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{displayStatus}</span>;
										})()}
										</div>
										<div className="col-span-2">
											<div className="text-base text-gray-600">{r.department}</div>
										</div>
										<div className="col-span-2">
											<div className="text-base text-gray-600">{r.designation}</div>
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</div>

					{/* Pagination - Only show when there are results */}
					{pageSize > 0 && filtered.length > 0 && (
						<div className="px-4 py-3 border-t border-gray-200">
							<Pagination
								page={page}
								pageCount={pageCount}
								onPageChange={(p) => setPage(p)}
								pageSize={pageSize}
								onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
								pageSizeOptions={PageSizeOptions}
						/>
						</div>
					)}
				</Card>
		</div>
	);
};

export default AppraisalAdminPage;
